import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSuiClient, useSignAndExecuteTransaction } from "@mysten/dapp-kit";
import { X, Upload, Rocket, Twitter, Send, Globe, Info, Loader2, ArrowRight, ArrowLeft, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";
import { useDeployment } from "@/context/DeploymentContext";
import { api, API } from "@/lib/api";
import { normalizeSocialUrl } from "@/lib/socials";
import { publishCoin, createCurve, buyOnCurve } from "@/lib/sui";

export default function CreateCoinModal({ open, onClose, onCreated }) {
  const { connected, address, refresh } = useWallet();
  const { deployed, deployment } = useDeployment();
  const client = useSuiClient();
  const { mutateAsync: signExec } = useSignAndExecuteTransaction();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", symbol: "", description: "", twitter: "", telegram: "", website: "", dev_buy: "" });
  const TOTAL_SUPPLY = 1000000000;
  const CREATE_FEE_MIST = 2_000_000_000;
  const [logo, setLogo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState("");
  const [stepNum, setStepNum] = useState(1);
  const fileRef = useRef();

  if (!open) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onFile = (e) => { const f = e.target.files[0]; if (!f) return; setLogo(f); setPreview(URL.createObjectURL(f)); };

  const submitOnchain = async () => {
    let logo_path = null, icon_url = "";
    if (logo) {
      setStep("Uploading logo…");
      const fd = new FormData(); fd.append("logo", logo); fd.append("owner", address);
      const { data } = await api.post("/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      logo_path = data.logo_path; icon_url = `${API}/files/${logo_path}`;
    }
    setStep("Compiling coin bytecode…");
    const { data: art } = await api.post("/contract/compile-coin", { name: form.name, symbol: form.symbol, decimals: 9, description: form.description, icon_url });
    setStep("Publishing coin — confirm in wallet (1/2)…");
    const pub = await publishCoin(signExec, client, { modules: art.modules, dependencies: art.dependencies }, address);
    setStep("Creating bonding curve — confirm in wallet (2/3)…");
    const totalSupplyBase = (BigInt(TOTAL_SUPPLY) * (10n ** 9n)).toString();
    let feeRecipient = deployment.platform || null;
    if (!feeRecipient) {
      try { const { data: cfg } = await api.get("/admin/config"); feeRecipient = cfg?.platform || null; } catch (e) {}
    }
    const cur = await createCurve(signExec, client, { pkg: deployment.package_id, configId: deployment.config_id, coinType: pub.coinType, treasuryCap: pub.treasuryCap, metadata: pub.metadata, totalSupplyBase, feeRecipient, createFeeMist: CREATE_FEE_MIST });
    const devBuy = Number(form.dev_buy) || 0;
    if (devBuy > 0) {
      setStep("Dev first buy — confirm in wallet (3/3)…");
      const amountMist = (BigInt(Math.floor(devBuy * 1e9))).toString();
      try {
        await buyOnCurve(signExec, client, { pkg: deployment.package_id, configId: deployment.config_id, curveId: cur.curveId, coinType: pub.coinType, amountMist, minOut: 0 });
      } catch (e) { toast.error("Dev first buy failed — coin still launched. " + (e?.message || "").slice(0, 80)); }
    }
    setStep("Registering…");
    await api.post("/coins/onchain", {
      curve_id: cur.curveId, coin_type: pub.coinType, package_id: deployment.package_id,
      name: form.name, symbol: form.symbol, description: form.description, logo_path,
      twitter: normalizeSocialUrl("twitter", form.twitter), telegram: normalizeSocialUrl("telegram", form.telegram), website: normalizeSocialUrl("website", form.website),
      decimals: 9, total_supply: TOTAL_SUPPLY, creator: address,
      publish_digest: pub.digest, create_digest: cur.digest,
    });
    toast.success(`🚀 $${form.symbol} launched on Sui mainnet!`);
    onCreated && onCreated();
    onClose();
    navigate(`/coin/${cur.curveId}`);
  };

  const submitPreview = async () => {
    const fd = new FormData();
    ["name", "symbol", "description"].forEach((k) => fd.append(k, form[k]));
    ["twitter", "telegram", "website"].forEach((k) => fd.append(k, normalizeSocialUrl(k, form[k])));
    fd.append("creator", address); fd.append("initial_buy_sui", 0);
    if (logo) fd.append("logo", logo);
    const { data } = await api.post("/coins", fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success(`Launched $${data.symbol} (preview)`);
    await refresh(address);
    onCreated && onCreated(data);
    onClose();
  };

  const next = () => {
    if (!form.name.trim() || !form.symbol.trim()) return toast.error("Name and symbol are required");
    setStepNum(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.symbol) { setStepNum(1); return toast.error("Name and symbol are required"); }
    if (!form.twitter.trim()) return toast.error("Twitter / X link is required");
    if (!form.telegram.trim()) return toast.error("Telegram link is required");
    if (!connected) return toast.error("Connect your Sui wallet first");
    setSubmitting(true);
    try {
      if (deployed) await submitOnchain(); else await submitPreview();
      setForm({ name: "", symbol: "", description: "", twitter: "", telegram: "", website: "", dev_buy: "" });
      setLogo(null); setPreview(null); setStepNum(1);
    } catch (err) {
      console.error(err);
      toast.error((err?.response?.data?.detail || err?.message || "Failed to launch coin").slice(0, 160));
    } finally { setSubmitting(false); setStep(""); }
  };

  const inputCls = "w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#1EC62E]/60 transition-colors";
  const sym = (form.symbol || "COIN").toUpperCase();

  const PreviewCard = ({ testid }) => (
    <div className="card-coin p-4" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="rounded-xl p-[1.5px] shrink-0" style={{ background: "linear-gradient(135deg,#1EC62E,#7CF24E)" }}>
          {preview
            ? <img src={preview} alt="" className="w-[52px] h-[52px] rounded-[10px] object-cover" />
            : <div className="w-[52px] h-[52px] rounded-[10px] grid place-items-center font-display font-extrabold text-black" style={{ background: "linear-gradient(135deg,#1EC62E,#7CF24E)" }}>{sym.slice(0, 3)}</div>}
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold text-[15px] truncate">{form.name || "Your coin"}</div>
          <div className="font-mono text-xs text-[#1EC62E]">${sym}</div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-3 line-clamp-2 h-8">{form.description || "Your coin description will appear here…"}</p>
      <div className="flex items-end justify-between mt-3 mb-3.5">
        <div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Market cap</div>
          <div className="font-display font-extrabold text-lg leading-none mt-1">—</div>
        </div>
        <div className="text-right"><div className="text-[10px] text-muted-foreground uppercase tracking-wide">24h vol</div><div className="font-mono text-xs text-[#1EC62E] mt-1">0.00</div></div>
      </div>
      <div className="w-full bg-foreground/10 rounded-full h-2.5 border border-white/10 p-0.5"><div className="bar-fill h-full rounded-full" style={{ width: "6%" }} /></div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="card-premium w-full max-w-3xl my-8 relative overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="create-coin-form">
        <div className="hero-mesh opacity-40" aria-hidden="true" />
        <button onClick={onClose} className="absolute right-4 top-4 z-10 p-1.5 rounded-lg bg-white/5 text-muted-foreground hover:text-white" data-testid="create-coin-close"><X className="w-5 h-5" /></button>

        <div className="relative grid md:grid-cols-[1fr_300px]">
          {/* ---------- FORM ---------- */}
          <div className="p-6 sm:p-7">
            <span className="eyebrow">{deployed ? "Sui Mainnet Launch" : "Preview Launch"}</span>
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl mt-3">Launch a Coin</h2>

            {/* ---------- LIVE PREVIEW (mobile) ---------- */}
            <div className="md:hidden mt-4">
              <span className="eyebrow mb-2">Live preview</span>
              <div className="mt-2"><PreviewCard testid="create-coin-preview-mobile" /></div>
            </div>

            {/* Stepper */}
            <div className="flex items-center gap-2 mt-4 mb-5">
              {[1, 2].map((n) => (
                <React.Fragment key={n}>
                  <div className={`flex items-center gap-2 text-xs font-bold ${stepNum >= n ? "text-[#1EC62E]" : "text-muted-foreground"}`}>
                    <span className={`grid place-items-center w-6 h-6 rounded-full border ${stepNum > n ? "bg-[#1EC62E] border-[#1EC62E] text-black" : stepNum === n ? "border-[#1EC62E]" : "border-white/15"}`}>
                      {stepNum > n ? <Check className="w-3.5 h-3.5" /> : n}
                    </span>
                    {n === 1 ? "Identity" : "Launch"}
                  </div>
                  {n === 1 && <div className="flex-1 h-px bg-white/10" />}
                </React.Fragment>
              ))}
            </div>

            <form onSubmit={submit} className="space-y-4">
              {stepNum === 1 ? (
                <>
                  <div className="flex gap-4 items-start">
                    <div onClick={() => fileRef.current.click()} className="w-24 h-24 shrink-0 rounded-2xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-[#1EC62E]/60 overflow-hidden bg-white/5 transition-colors" data-testid="logo-upload-zone">
                      {preview ? <img src={preview} alt="preview" className="w-full h-full object-cover" /> : <><Upload className="w-5 h-5 text-muted-foreground" /><span className="text-[10px] text-muted-foreground mt-1">Logo</span></>}
                    </div>
                    <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="logo-file-input" />
                    <div className="flex-1 space-y-3">
                      <input data-testid="coin-name-input" value={form.name} onChange={set("name")} placeholder="Coin name (e.g. Sui Pepe)" className={inputCls} />
                      <input data-testid="coin-symbol-input" value={form.symbol} onChange={set("symbol")} placeholder="Symbol (e.g. SUIPEP)" maxLength={10} className={`${inputCls} font-mono`} />
                    </div>
                  </div>
                  <textarea data-testid="coin-description-input" value={form.description} onChange={set("description")} placeholder="Description — tell degens why they should ape in" rows={3} className={`${inputCls} resize-none`} />
                  <div className="grid grid-cols-1 gap-2">
                    <div className="relative"><Twitter className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><input data-testid="coin-twitter-input" value={form.twitter} onChange={set("twitter")} placeholder="Twitter / X (handle or URL, required)" className={`${inputCls} pl-9`} /></div>
                    <div className="relative"><Send className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><input data-testid="coin-telegram-input" value={form.telegram} onChange={set("telegram")} placeholder="Telegram (handle or URL, required)" className={`${inputCls} pl-9`} /></div>
                    <div className="relative"><Globe className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><input data-testid="coin-website-input" value={form.website} onChange={set("website")} placeholder="Website URL (optional)" className={`${inputCls} pl-9`} /></div>
                  </div>
                  <div className="flex items-center gap-3 pt-1">
                    <button type="button" onClick={onClose} className="btn-ghost px-5 py-2.5 text-sm">Cancel</button>
                    <button type="button" onClick={next} data-testid="create-next-button" className="btn-primary flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm">Continue <ArrowRight className="w-4 h-4" /></button>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">Total supply</label>
                      <div data-testid="total-supply-fixed" className="w-full mt-1 bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm font-mono text-muted-foreground flex items-center justify-between">
                        <span>1,000,000,000</span><span className="text-[10px] text-[#1EC62E]">fixed</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">Dev buy (SUI) <span className="text-muted-foreground/60 normal-case">optional</span></label>
                      <input data-testid="dev-buy-input" type="number" min="0" step="0.01" value={form.dev_buy} onChange={set("dev_buy")} placeholder="0.0" className={`${inputCls} mt-1 font-mono`} />
                    </div>
                  </div>
                  {deployed && Number(form.dev_buy) > 0 && (
                    <div className="flex gap-2 items-start text-[11px] text-[#FFB800] bg-[#FFB800]/10 rounded-xl p-2.5 border border-[#FFB800]/25">
                      <Info className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>You'll buy {form.dev_buy} SUI of ${sym} first — marks it "Dev bought first" on-chain (3rd wallet signature).</span>
                    </div>
                  )}
                  <div className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-2.5 text-xs">
                    <div className="flex justify-between"><span className="text-muted-foreground">Graduation target</span><span className="font-mono font-bold">7,500 SUI → Cetus</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Trade fee</span><span className="font-mono">1% · 0.7% dev / 0.3% platform</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Launch fee</span><span className="font-mono">{deployed ? "2 SUI + gas" : "— (preview)"}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Liquidity</span><span className="font-mono text-[#1EC62E]">Locked forever on migrate</span></div>
                  </div>
                  <div className="flex gap-2 items-start text-[11px] text-muted-foreground bg-white/5 rounded-xl p-3 border border-white/10">
                    <Info className="w-4 h-4 shrink-0 text-[#1EC62E] mt-0.5" />
                    <span>{deployed ? `Publish the coin, then create the curve${Number(form.dev_buy) > 0 ? ", then your dev buy" : ""} — wallet-signed. 1% trade fee enforced on-chain.` : "Preview mode — deploy the launchpad first for real on-chain launches."}</span>
                  </div>
                  <div className="flex items-center gap-3 pt-1">
                    <button type="button" onClick={() => setStepNum(1)} className="btn-ghost inline-flex items-center gap-1.5 px-5 py-2.5 text-sm"><ArrowLeft className="w-4 h-4" /> Back</button>
                    <button type="submit" disabled={submitting} data-testid="create-coin-submit-button" className="btn-primary flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm disabled:opacity-50">
                      {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> {step || "Launching…"}</> : <><Rocket className="w-4 h-4" /> {deployed ? "Launch Now" : "Launch (preview)"}</>}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>

          {/* ---------- LIVE PREVIEW (desktop) ---------- */}
          <div className="relative hidden md:flex flex-col gap-3 p-6 border-l border-white/10 bg-white/[0.02]">
            <span className="eyebrow">Live preview</span>
            <PreviewCard testid="create-coin-preview" />
            <div className="flex items-start gap-2 text-[11px] text-muted-foreground mt-1">
              <Sparkles className="w-4 h-4 shrink-0 text-[#1EC62E] mt-0.5" />
              <span>This is exactly how your coin appears on the board the moment it launches.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
